/**
 * Widget « Ma progression » de la home — extrait compact du graphe de compétences
 * (le spider complet vit dans la page Progrès). Langage 100 % positif :
 * « ta force » (phase la plus haute) + « tu montes / ta prochaine marche »
 * (phase en hausse, sinon la plus proche de la maîtrise). Jamais de « point faible ».
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, ArrowRight } from "lucide-react";
import { competencyApi, type CompetencyPhase } from "../lib/api";

const MASTERY_TARGET = 80;
const R = 34;
const CX = 40;
const CY = 40;

function pt(i: number, frac: number): [number, number] {
  const a = ((-90 + i * 72) * Math.PI) / 180;
  const r = frac * R;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}
function ring(frac: number): string {
  return [0, 1, 2, 3, 4].map((i) => pt(i, frac).join(",")).join(" ");
}

export function HomeProgressWidget({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { t } = useTranslation();
  const [phases, setPhases] = useState<CompetencyPhase[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    competencyApi
      .getMine()
      .then((d) => { if (alive) setPhases(d.phases); })
      .catch(() => { if (alive) setPhases(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const phaseName = (k: string) => t(`weeklyRhythm.phase.${k}`);
  const withData = (phases || []).filter((p) => p.score != null);

  // Radar : points (0 si pas de data pour garder la forme).
  const dataPoints = (phases || []).map((p, i) => pt(i, (p.score ?? 0) / 100).join(",")).join(" ");

  // Ta force = phase la plus haute.
  const force = withData.length
    ? withData.reduce((a, b) => (a.score! >= b.score! ? a : b))
    : null;

  // Tu montes = meilleure tendance positive ; sinon la plus proche de la maîtrise.
  const rising = withData
    .filter((p) => (p.trend ?? 0) > 0)
    .sort((a, b) => (b.trend ?? 0) - (a.trend ?? 0))[0] || null;
  const toClimb = withData.filter((p) => (p.score ?? 0) < MASTERY_TARGET)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0] || null; // la plus haute encore < 80 = prochaine marche atteignable

  return (
    <div className="flex h-full flex-col rounded-2xl border bg-white p-4 md:p-5 shadow-[var(--elev-1)]">
      <div className="mb-3 flex items-center gap-1.5">
        <TrendingUp className="h-4 w-4" style={{ color: "#384872" }} />
        <h3 className="font-semibold text-sm" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          {t("homeProgress.title")}
        </h3>
      </div>

      {!loading && withData.length === 0 ? (
        <p className="flex-1 text-sm text-muted-foreground">{t("homeProgress.empty")}</p>
      ) : (
        <div className="flex flex-1 items-center gap-4">
          {/* Mini-radar */}
          <svg viewBox="0 0 80 80" width="78" height="78" className="shrink-0" role="img" aria-label={t("homeProgress.title")}>
            {[0.5, 1].map((f) => (
              <polygon key={f} points={ring(f)} fill="none" stroke="#E7E9F0" strokeWidth="1" />
            ))}
            <polygon points={dataPoints} fill="rgba(124,191,148,.28)" stroke="#7CBF94" strokeWidth="2" />
          </svg>

          {/* Lignes positives */}
          <div className="min-w-0 text-xs" style={{ color: "#5A6076" }}>
            {force && (
              <div className="mb-1">
                🎯 {t("homeProgress.force")} : <b style={{ color: "#384872" }}>{phaseName(force.phaseKey)}</b>
              </div>
            )}
            {rising ? (
              <div>
                <span style={{ color: "#1E7A3C", fontWeight: 700 }}>🚀 {t("homeProgress.rising")}</span> : {phaseName(rising.phaseKey)}{" "}
                <b style={{ color: "#384872" }}>{t("homeProgress.thisWeek", { n: rising.trend })}</b>
              </div>
            ) : toClimb ? (
              <div>
                <span style={{ color: "#1E7A3C", fontWeight: 700 }}>🚀 {t("homeProgress.nextStep")}</span> : {phaseName(toClimb.phaseKey)}{" "}
                <b style={{ color: "#384872" }}>{t("homeProgress.toMaster", { n: MASTERY_TARGET - (toClimb.score ?? 0) })}</b>
              </div>
            ) : force ? (
              <div style={{ color: "#1E7A3C", fontWeight: 600 }}>{t("homeProgress.allMastered")}</div>
            ) : null}
          </div>
        </div>
      )}

      <button
        onClick={() => { try { localStorage.setItem("bizcoach_progress_tab", "competences"); } catch { /* noop */ } onNavigate?.("my-progress"); }}
        // `min-h-[36px]` : sans hauteur minimale, le lien ne faisait que 16 px —
        // la hauteur de sa ligne de texte. Trop petit pour un doigt, et sous le
        // seuil que `play-responsive.spec.ts` fait respecter à l'accueil.
        className="mt-3 inline-flex min-h-[36px] items-center gap-1 self-start py-1 text-xs font-semibold"
        style={{ color: "#5A4BBE" }}
      >
        {t("homeProgress.link")}<ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
